import * as z from 'zod'

import { ValuePeriod } from '#domain/types'

import { idNoUnderscoreSchema, nonEmptyTrimmedStringSchema } from './primitives'

const valuePeriodSchema = z.enum(ValuePeriod)

const questionBaseSchema = z.strictObject({
  id: idNoUnderscoreSchema,
  title: z.string().nullish(),
  info: z.string().nullish(),
  isOptional: z.boolean().optional(),
  renderIf: z.string().optional(),
  placeholder: z.string().nullish(),
})

const stringQuestionSchema = questionBaseSchema.extend({
  type: z.literal('string'),
  defaultValue: z.string().optional(),
})

export type StringQuestion = z.output<typeof stringQuestionSchema>

const integerQuestionSchema = questionBaseSchema.extend({
  type: z.literal('integer'),
  min: z.number().optional(),
  max: z.number().optional(),
  defaultValue: z.number().nullish(),
})

export type IntegerQuestion = z.output<typeof integerQuestionSchema>

const moneyQuestionSchema = questionBaseSchema.extend({
  type: z.literal('money'),
  min: z.number().optional(),
  max: z.number().optional(),
  defaultValue: z.number().nullish(),
})

export type MoneyQuestion = z.output<typeof moneyQuestionSchema>

const moneyWithPeriodQuestionSchema = questionBaseSchema.extend({
  type: z.literal('moneyWithPeriod'),
  min: z.number().optional(),
  max: z.number().optional(),
  defaultValue: z.number().nullish(),
  // Optional in the YAML; the `.default` means `defaultPeriod` is always a
  // ValuePeriod after parse, so consumers never need a `?? yearly` fallback.
  defaultPeriod: valuePeriodSchema.default(ValuePeriod.YEARLY),
})

export type MoneyWithPeriodQuestion = z.output<
  typeof moneyWithPeriodQuestionSchema
>

const percentageQuestionSchema = questionBaseSchema.extend({
  type: z.literal('percentage'),
  min: z.number().optional(),
  max: z.number().optional(),
  defaultValue: z.number().nullish(),
})

export type PercentageQuestion = z.output<typeof percentageQuestionSchema>

const booleanQuestionSchema = questionBaseSchema
  .omit({ placeholder: true })
  .extend({
    type: z.literal('boolean'),
    mode: z.enum(['toggle', 'checkbox'], 'invalid mode').optional(),
    title: z.string(),
    defaultValue: z.boolean().optional(),
  })

export type BooleanQuestion = z.output<typeof booleanQuestionSchema>

export const singleSelectOptionSchema = z.union([
  z.string().min(1),
  z.object({
    label: nonEmptyTrimmedStringSchema,
    value: nonEmptyTrimmedStringSchema,
    renderIf: z.string().optional(),
  }),
])

export type SingleSelectOption = z.infer<typeof singleSelectOptionSchema>

const getSingleSelectOptionValues = (options: SingleSelectOption[]): string[] =>
  options.map(opt => (typeof opt === 'string' ? opt : opt.value))

const singleSelectQuestionSchema = questionBaseSchema
  .extend({
    type: z.literal('singleSelect'),
    options: z.array(singleSelectOptionSchema).min(1),
    defaultValue: z.string().nullish(),
  })
  .refine(
    data => {
      if (data.defaultValue == null || data.defaultValue === '') return true
      const options = data.options
      if (!options || !Array.isArray(options)) return true
      const validValues = getSingleSelectOptionValues(options)
      return validValues.includes(data.defaultValue)
    },
    {
      message: 'defaultValue is not in the options list',
      path: ['defaultValue'],
    }
  )

export type SingleSelectQuestion = z.output<typeof singleSelectQuestionSchema>

export const categorySelectQuestionSchema = questionBaseSchema.extend({
  type: z.literal('categorySelect'),
  options: z.array(z.string()).min(1),
})

export type CategorySelectQuestion = z.output<
  typeof categorySelectQuestionSchema
>

const dateQuestionSchema = questionBaseSchema.extend({
  type: z.literal('date'),
})

export type DateQuestion = z.output<typeof dateQuestionSchema>

const zipCodeQuestionSchema = questionBaseSchema.extend({
  type: z.literal('zipCode'),
})

export type ZipCodeQuestion = z.output<typeof zipCodeQuestionSchema>

const ownerQuestionSchema = questionBaseSchema.extend({
  type: z.literal('owner'),
  showJoint: z.boolean().optional(),
})

export type OwnerQuestion = z.output<typeof ownerQuestionSchema>

const VALID_ANSWER_TYPES = [
  'string',
  'integer',
  'money',
  'moneyWithPeriod',
  'boolean',
  'singleSelect',
  'categorySelect',
  'date',
  'zipCode',
  'percentage',
  'owner',
] as const

export const questionSchema = z.discriminatedUnion(
  'type',
  [
    stringQuestionSchema,
    integerQuestionSchema,
    moneyQuestionSchema,
    moneyWithPeriodQuestionSchema,
    percentageQuestionSchema,
    booleanQuestionSchema,
    singleSelectQuestionSchema,
    categorySelectQuestionSchema,
    dateQuestionSchema,
    zipCodeQuestionSchema,
    ownerQuestionSchema,
  ],
  'question type is required, available types: ' + VALID_ANSWER_TYPES.join(', ')
)

export type Question = z.output<typeof questionSchema>

export default questionSchema
